/**
 * Home workspace navigation state (Zustand, partially persisted).
 *
 * The three-pane Home (Patienten · Gespräche · geöffnetes Gespräch) tracks WHERE
 * the nurse is: which scope tab, which patient, which conversation is open. This
 * lived in Home's local `useState`, so leaving Home reset it — and Home is left
 * on almost every action: opening an agent is either an inline route to
 * `/sessions/:id` (unmounts Home) or a full-page `window.location` hop to a
 * sub-app host (compat, care-planner, roster, …, which wipes ALL in-memory state), and the
 * header links (Notices, Knowledge, Admin, Profile) unmount it too. On the way
 * back the selection was gone and the rail snapped to the FIRST patient
 * (`selectedPatientId ?? currentPatients[0]`), not the patient she was working on.
 *
 * Lifting the state here fixes it in ONE place for every one of those exits:
 *   - `scope` + `selectedPatientId` are PERSISTED to localStorage (same idiom as
 *     uiPrefs), so they survive a full-page sub-app hop / PWA reload — in-memory
 *     state alone would not, since those are real browser navigations.
 *   - `selectedConversationId` is PERSISTED too, and so is `selectedAgentFace`:
 *     the agent an open-with-no-session launch is showing in pane 3 BEFORE a
 *     session exists (see Home.tsx launchAgent / AgentFacePane — sessions are
 *     minted lazily, on the first chat message, not on tile tap). They are
 *     mutually exclusive: each setter clears the other, so pane 3 never has to
 *     reconcile both being set at once.
 *
 *     They USED to be in-memory only, to keep a hard reload from drilling into a
 *     possibly-stale conversation on the mobile one-pane view. That cost more
 *     than it bought: a reload dropped every trace of the agent the nurse was in
 *     — a launched FACE mints no session, so nothing else recorded it — and pane
 *     3 fell back to "the scope's newest conversation", i.e. a DIFFERENT agent.
 *     Measured on usb: open Lernen or Expert from Persönlich, reload, land in the
 *     Y-Site compatibility consult that happened to be newest.
 *
 *     A stale conversation id is handled where it already was — missing from the
 *     live scope list, so Home falls back to the scope default. A face needs no
 *     equivalent: it holds no patient (see AgentFaceSelection) and Home derives
 *     its frame, so the only thing that can rot is the SLUG. Which makes this
 *     the surface a future agent rename has to remember: `sessions.agent_slug`
 *     gets a DB migration (see #2427 / db/migrations/api/045), and localStorage
 *     cannot be reached by SQL — a rename must bump this store's persist
 *     `version` with a `migrate` mapping old→new, or every nurse holding the old
 *     slug silently loses the agent she had open.
 *
 *     Home holds a floor UNDER that, not a replacement for it: a face whose slug
 *     the agent-detail registry no longer has is dropped on read and pane 3 uses
 *     the scope default (resolveEffectiveAgentFace). It is what keeps a
 *     forgotten migration — or an agent REMOVED, where there is nothing to map
 *     to — from leaving her on a pane that renders nothing until she taps
 *     something. It cannot restore the face; only the migration can.
 *   - `selectionTouched` is the in-memory half of that trade: true once the nurse
 *     has actively picked something in THIS app load. A RESTORED selection fills
 *     pane 3 side-by-side on desktop, but must not auto-drill a phone straight
 *     into the detail pane on a cold PWA launch — she opens the app at the
 *     bedside wanting her patient list. Home's `data-level` gates on it.
 *
 * Persisted IDs can go stale (patient left the shift). The store holds raw intent
 * only; Home validates it against the live lists it already has (currentPatients)
 * and falls back to the scope default — effect-free, the same derive-don't-sync
 * approach Home uses everywhere else.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import { STORAGE_KEY_HOME_NAV } from '@/lib/constants'

/** Home scope tabs: patient work, the nurse's own (Persönlich), ward-level (Team). */
export type HomeScope = 'patient' | 'personal' | 'team'

/** The agent an open, session-less FACE is showing. Just the slug: the patient
 *  frame is NOT stored here. A launch writes the patient to `selectedPatientId`
 *  in the same call, so a copy on the face would be the same fact twice — and
 *  the two can then disagree after a reload, since the rail re-resolves a stored
 *  patient against the live shift. Home hands the face the frame it derives for
 *  every other pane, so there is one patient and nothing to reconcile. */
export interface AgentFaceSelection {
  slug: string
}

interface HomeNavState {
  /** Active scope tab. Persisted. */
  scope: HomeScope
  /** The patient whose conversations pane 2/3 show. Persisted (validated by Home
   *  against the current shift; a stale id falls back to the first patient). */
  selectedPatientId: string | null
  /** The open conversation in pane 3. Persisted (validated by Home against the
   *  active scope's live list; a stale id falls back to the scope default). */
  selectedConversationId: string | null
  /** The open, session-less agent face in pane 3. Persisted; the only thing on
   *  it that can rot is the slug, and Home drops a face whose agent the detail
   *  registry no longer has (resolveEffectiveAgentFace) so pane 3 falls back to
   *  the scope default. That is a floor, not a migration — see the module
   *  docstring on what an agent rename still owes this store. */
  selectedAgentFace: AgentFaceSelection | null
  /** True once the nurse picked a conversation/face in THIS app load. Never
   *  persisted — see the module docstring (mobile must not auto-drill). */
  selectionTouched: boolean
  setScope: (scope: HomeScope) => void
  setSelectedPatientId: (id: string | null) => void
  /** Also clears `selectedAgentFace` — the two are mutually exclusive. */
  setSelectedConversationId: (id: string | null) => void
  /** Also clears `selectedConversationId` — the two are mutually exclusive. */
  setSelectedAgentFace: (face: AgentFaceSelection | null) => void
  /**
   * Record that the nurse navigated within pane 3 WITHOUT changing which
   * conversation or face it holds — she pushed into a screen of the agent
   * already open (AgentFrame's route tree, useAgentNavigate).
   *
   * It matters on a phone, where `data-level` shows one pane at a time: the
   * push is exactly the "she actively picked something" that lets Home drill
   * from the list to the detail pane. Without it the screen she asked for
   * mounts off-screen behind the list she was just looking at.
   */
  markSelectionTouched: () => void
  /** Clear all selection back to defaults. Called on logout so the persisted
   *  patient id never carries into the next nurse's session on a shared device. */
  reset: () => void
}

const INITIAL: Pick<
  HomeNavState,
  | 'scope'
  | 'selectedPatientId'
  | 'selectedConversationId'
  | 'selectedAgentFace'
  | 'selectionTouched'
> = {
  scope: 'patient',
  selectedPatientId: null,
  selectedConversationId: null,
  selectedAgentFace: null,
  selectionTouched: false,
}

export const useHomeNavStore = create<HomeNavState>()(
  persist(
    (set) => ({
      ...INITIAL,
      setScope: (scope) => set({ scope }),
      setSelectedPatientId: (selectedPatientId) => set({ selectedPatientId }),
      setSelectedConversationId: (selectedConversationId) =>
        set({ selectedConversationId, selectedAgentFace: null, selectionTouched: true }),
      setSelectedAgentFace: (selectedAgentFace) =>
        set({ selectedAgentFace, selectedConversationId: null, selectionTouched: true }),
      markSelectionTouched: () => set({ selectionTouched: true }),
      reset: () => set({ ...INITIAL }),
    }),
    {
      name: STORAGE_KEY_HOME_NAV,
      storage: createJSONStorage(() => localStorage),
      // The whole "where am I" position, so a reload restores it. selectionTouched
      // is the one field deliberately excluded — see the module docstring.
      partialize: (s) => ({
        scope: s.scope,
        selectedPatientId: s.selectedPatientId,
        selectedConversationId: s.selectedConversationId,
        selectedAgentFace: s.selectedAgentFace,
      }),
    },
  ),
)
